import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { setRequestLocale } from '@/shared/api';
import { Button, LanguageIcon } from '@/shared/ui';

type LocaleCode = 'fa' | 'en';

/**
 * One button that names the *other* language, in that language's own script.
 *
 * A picker that translates its own options is unusable to the one person who
 * needs it, so the label is «English» on the فارسی screen and «فارسی» on the
 * English one — whoever cannot read the current page can still read the way out.
 */
export function LocaleToggle(): JSX.Element {
  const { t, i18n } = useTranslation('common');
  const next: LocaleCode = i18n.language === 'en' ? 'fa' : 'en';

  return (
    <Button
      aria-label={`${t('locale.label')}: ${t(`locale.${next}`)}`}
      className="tap-target !px-2"
      onClick={(): void => {
        void i18n.changeLanguage(next);
        setRequestLocale(next);
      }}
      size="sm"
      variant="ghost"
    >
      <LanguageIcon className="size-5" />
      <span className="type-caption font-medium" lang={next}>
        {t(`locale.${next}`)}
      </span>
    </Button>
  );
}
